// src/FilingGenerator.js — UPDATED with tool tabs
// 
// WHAT CHANGED:
//   - Added 4 tabs at top: Form 5472, Annual Report, EIN Application, ITIN Package
//   - Active tab switches the form in the main area
//   - Sidebar stays exactly the same — history still works for 5472
//   - Other 3 tools render inside the same shell, no route changes needed
//   - App.js needs zero changes
//   - Added fax-delivery polling (checkFaxDelivery / applyDeliveryUpdate / resumeFaxTracking)
//     so "faxed" status auto-updates to "delivered" without a manual refresh

import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { usePlanTracker, FILING_STATUS } from '../hooks/usePlanTracker';
import useFilingGenerator from '../hooks/useFilingGenerator';
import { FilingForm }     from './FilingForm';
import { FilingPreview }  from './FilingPreview';
import { FilingResults }  from './FilingResults';
import { EINApplication } from './EINApplication';
import { ITINApplication } from './ITINApplication';
import { AnnualReport } from './AnnualReport';
import Sidebar from './Sidebar';
import AuthModal from '../AuthModal';
import './TaxApp.css';

const STEP_FORM    = 'form';
const STEP_PREVIEW = 'preview';
const STEP_RESULT  = 'result';

const EMPTY_FORM = {
  llcName: '', ein: '', taxYear: '',
  ownerName: '', ownerEmail: '', ownerCountry: '', ownerAddress: '',
  city: '', stateCode: '', country: '',
  ownerTitle: '',
  businessActivity: '', businessCode: '',
  isFirstYear: false,
  signatureBase64: null,
  statementText: null, statementName: '', statementSize: 0,
};

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  {
    id:      'form5472',
    label:   'Form 5472 & Form 1120',
    penalty: '',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  
  {
    id:      'ein',
    label:   'EIN Application',
    penalty: '',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07
          19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3
          a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09
          9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0
          2.81.7A2 2 0 0 1 22 16.92z"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id:      'itin',
    label:   'ITIN Package',
    penalty: '',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
      </svg>
    ),
  },



  
];


const FREE_TAB = {
  id:    'annualReport',
  label: 'Annual Report',
  free:  true,
  icon: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
      <path d="M3 10h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  ),
};

// ── Tab bar component ─────────────────────────────────────────────────────────
// Replace the existing ToolTabs function in FilingGenerator.js with this.

function ToolTabs({ activeTab, onTabChange, isInSubStep }) {
  const renderTab = (tab) => (
    <button
      key={tab.id}
      className={`tool-tab ${activeTab === tab.id ? 'tool-tab--active' : ''}`}
      onClick={() => onTabChange(tab.id)}
      type="button"
      disabled={isInSubStep && activeTab !== tab.id}
      title={isInSubStep && activeTab !== tab.id ? 'Complete or cancel current filing first' : tab.label}
    >
      <span className="tool-tab-icon">{tab.icon}</span>
      <span className="tool-tab-label">{tab.label}</span>
      {tab.free && <span className="tool-tab-free-badge">FREE</span>}
      {!tab.free && tab.penalty && <span className="tool-tab-penalty">{tab.penalty}</span>}
    </button>
  );

  return (
    <div className="tool-tabs">
      {TABS.map(renderTab)}
      {renderTab(FREE_TAB)}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function FilingGenerator() {
  const { user } = useAuth();

  const planTracker = usePlanTracker(user?.uid);
  const usageStats  = planTracker?.getUsageStats();

  const {
    isProcessing, processingError,
    filingData, setFilingData,
    transactions, setTransactions,
    generatedPdfBase64, setGeneratedPdfBase64,
    faxStatus, faxConfirmation,
    parseStatement, generatePDF, sendFax, downloadPDF, clearAll,
    checkFaxDelivery, applyDeliveryUpdate, resumeFaxTracking,
  } = useFilingGenerator(planTracker);

  const [activeTab,          setActiveTab         ] = useState('form5472');
  const [step,               setStep              ] = useState(STEP_FORM);
  const [formData,           setFormData          ] = useState(EMPTY_FORM);
  const [showAuthModal,      setShowAuthModal      ] = useState(false);
  const [authMode,           setAuthMode          ] = useState('login');
  const [currentSessionId,   setCurrentSessionId  ] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal ] = useState({ show: false, sessionId: null });
  // Holds an Annual Report/EIN/ITIN history record clicked in the sidebar, so that
  // tool's tab can render its confirmation screen instead of a blank form.
  // (Form 5472 doesn't need this — it already has its own STEP_PREVIEW/
  // STEP_RESULT flow driven by filingData/transactions/faxConfirmation.)
  const [loadedToolSession,  setLoadedToolSession ] = useState(null);

  // Only disable tab switching if user is mid-flow in 5472 (preview or result)
  const isInSubStep = activeTab === 'form5472' && step !== STEP_FORM;

  const handleTabChange = (tabId) => {
    if (isInSubStep && tabId !== 'form5472') return; // guard
    setActiveTab(tabId);
  };

  // ── 5472 handlers (unchanged from your original) ─────────────────────────
  const handleFormSubmit = async () => {
    if (!user) { setShowAuthModal(true); setAuthMode('login'); return; }
    try {
      const { transactions: parsedTx, formData: parsedForm } = await parseStatement(formData);
      const pdf = await generatePDF(parsedTx, parsedForm);
      const title = `${parsedForm.llcName} — Form 5472 — ${parsedForm.taxYear}`;
      const id    = await planTracker.createDraft(title, parsedForm, parsedTx, pdf);
      if (id) setCurrentSessionId(id);
      setStep(STEP_PREVIEW);
    } catch {
      // processingError state is set inside parseStatement/generatePDF
    }
  };

  const handleRegeneratePDF = useCallback(async (updatedTx, updatedFormData) => {
    const pdf = await generatePDF(updatedTx, updatedFormData);
    if (pdf && currentSessionId) {
      await planTracker.updateDraft(currentSessionId, updatedFormData, updatedTx, pdf);
    }
    return pdf;
  }, [generatePDF, planTracker, currentSessionId]);

  const handleSendToIRS = async (updatedFormData, updatedTransactions) => {
    try {
      let pdfToSend = generatedPdfBase64;
      if (updatedFormData || updatedTransactions) {
        pdfToSend = await handleRegeneratePDF(
          updatedTransactions || transactions,
          updatedFormData     || formData
        );
      }
      const confirmation = await sendFax(pdfToSend || generatedPdfBase64);
      if (currentSessionId) {
        await planTracker.markFaxed(currentSessionId, confirmation?.faxId || confirmation);
      }
      setStep(STEP_RESULT);
    } catch {
      // processingError state is set inside generatePDF/sendFax
    }
  };

  // ── Poll FaxDrop for delivery status while a fax is in transit ───────────
  useEffect(() => {
    if (faxStatus !== 'faxed' || !faxConfirmation?.faxId) return;

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 40; // ~10 minutes at 15s intervals

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;

      const statusData = await checkFaxDelivery(faxConfirmation.faxId);
      if (cancelled || !statusData) return;

      if (statusData.status === 'delivered' || statusData.status === 'failed') {
        applyDeliveryUpdate(statusData);

        if (currentSessionId && statusData.status === 'delivered') {
          await planTracker.markDelivered(currentSessionId, {
            faxId:       statusData.faxId,
            status:      'Delivered',
            delivered:   true,
            confirmedAt: statusData.deliveredAt || new Date().toISOString(),
            pages:       statusData.pages,
          });
        }

        clearInterval(intervalId);
      } else if (attempts >= MAX_ATTEMPTS) {
        clearInterval(intervalId); // give up politely, UI stays on "faxed"
      }
    };

    const intervalId = setInterval(poll, 15000);
    poll(); // also check immediately, don't wait 15s for the first check

    return () => { cancelled = true; clearInterval(intervalId); };
  }, [faxStatus, faxConfirmation?.faxId, currentSessionId, checkFaxDelivery, applyDeliveryUpdate, planTracker]);

  const handleStartOver = () => {
    setFormData(EMPTY_FORM);
    setStep(STEP_FORM);
    setCurrentSessionId(null);
    setLoadedToolSession(null);
    clearAll();
  };

  const loadSession = (session) => {
    if (!session) return;
    setCurrentSessionId(session.id);

    // Records written before this fix (or the 5472 flow, which never sets
    // `type`) default to '5472'. Annual Report/EIN/ITIN records carry their own type
    // since recordToolFiling started stamping it.
    const type = session.type || '5472';

    if (type !== 'form5472' && type !== '5472') {
      // Annual Report / EIN / ITIN — these tools are single-shot and self-contained,
      // so just switch to their tab and hand them the stored record. Each
      // tool renders its own confirmation screen from loadedRecord.
      setActiveTab(type);
      setLoadedToolSession(session);
      return;
    }

    setActiveTab('form5472');
    setLoadedToolSession(null);
    if (session.formData) {
      setFormData({ ...EMPTY_FORM, ...session.formData });
      setFilingData(session.formData);
    }
    if (session.transactions) setTransactions(session.transactions);
    if (session.pdfBase64)    setGeneratedPdfBase64(session.pdfBase64);
    if (session.status === FILING_STATUS.FAXED) {
      // Delivery was never confirmed before this tab was closed/reopened.
      // Re-check with FaxDrop right away instead of showing stale "Faxed" forever.
      resumeFaxTracking(session.faxId, session.faxConfirmation);
      setStep(STEP_RESULT);
    } else if (session.status === FILING_STATUS.DELIVERED) {
      // Already confirmed — just restore the confirmation banner, no re-check needed.
      applyDeliveryUpdate({
        status:      'delivered',
        faxId:       session.faxId,
        deliveredAt: session.faxConfirmation?.confirmedAt,
        pages:       session.faxConfirmation?.pages,
      });
      setStep(STEP_RESULT);
    } else {
      setStep(STEP_PREVIEW);
    }
  };

  const handleDeleteSession = (sessionId, e) => {
    e.stopPropagation();
    setDeleteConfirmModal({ show: true, sessionId });
  };

  const confirmDelete = async () => {
    const { sessionId } = deleteConfirmModal;
    setDeleteConfirmModal({ show: false, sessionId: null });
    const ok = await planTracker.deleteFiling(sessionId);
    if (ok && currentSessionId === sessionId) handleStartOver();
  };

  const cancelDelete = () => setDeleteConfirmModal({ show: false, sessionId: null });

  return (
    <div className="app-wrapper">

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onSwitchMode={setAuthMode}
      />

      {deleteConfirmModal.show && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header"><h3>Delete filing</h3></div>
            <div className="delete-modal-body">
              <p>Remove this filing from history? This won't affect what was sent to the IRS.</p>
            </div>
            <div className="delete-modal-footer">
              <button className="modal-btn modal-btn-cancel" onClick={cancelDelete}>Cancel</button>
              <button className="modal-btn modal-btn-delete" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <Sidebar
        user={user}
        chatHistory={planTracker.history}
        isHistoryLoading={planTracker.loading}
        currentSessionId={currentSessionId}
        usageStats={usageStats}
        planTracker={planTracker}
        onNewChat={handleStartOver}
        onLoadSession={loadSession}
        onDeleteSession={handleDeleteSession}
        onLogout={() => {}}
        getRelativeTime={planTracker.getRelativeTime}
      />

      <div className={`app-container ${step === STEP_FORM ? '' : 'has-results'} ${!user ? 'no-sidebar' : ''}`}>

        {/* ── Tab bar — always visible, sits above the form ── */}
        {/* Hide tabs when in preview/result to avoid confusion */}
        {step === STEP_FORM && (
          <ToolTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isInSubStep={isInSubStep}
          />
        )}

        {/* ── 5472 tool ── */}
        {activeTab === 'form5472' && (
          <>
            {step === STEP_FORM && (
              <FilingForm
                user={user}
                usageStats={usageStats}
                planTracker={planTracker}
                formData={formData}
                setFormData={setFormData}
                isProcessing={isProcessing}
                processingError={processingError}
                onSubmit={handleFormSubmit}
              />
            )}
            {step === STEP_PREVIEW && (
              <FilingPreview
                filingData={filingData || formData}
                transactions={transactions}
                setTransactions={setTransactions}
                generatedPdfBase64={generatedPdfBase64}
                isProcessing={isProcessing}
                processingError={processingError}
                onSendToIRS={handleSendToIRS}
                onBack={() => setStep(STEP_FORM)}
                onDownload={downloadPDF}
                onRegeneratePDF={handleRegeneratePDF}
              />
            )}
            {step === STEP_RESULT && (
              <FilingResults
                filingData={filingData || formData}
                transactions={transactions}
                faxStatus={faxStatus}
                faxConfirmation={faxConfirmation}
                generatedPdfBase64={generatedPdfBase64}
                onDownloadPDF={downloadPDF}
                onStartOver={handleStartOver}
              />
            )}
          </>
        )}

        {/* ── Other 3 tools — each self-contained ── */}
        {/* Only hand a tool the loadedToolSession if it's actually that
            tool's record — guards against a stale session from a different
            tab bleeding in when the user switches tabs manually. */}
        {/* Annual Report is fully client-side and free for everyone — no
            planTracker/credit check, no filing_history record, so there's
            nothing to load from a past session here. */}
        {activeTab === 'annualReport' && (
          <AnnualReport user={user} />
        )}
        {activeTab === 'ein' && (
          <EINApplication
            planTracker={planTracker}
            loadedRecord={loadedToolSession?.type === 'ein' ? loadedToolSession : null}
            onClearLoadedRecord={() => setLoadedToolSession(null)}
          />
        )}
        {activeTab === 'itin' && (
          <ITINApplication
            planTracker={planTracker}
            loadedRecord={loadedToolSession?.type === 'itin' ? loadedToolSession : null}
            onClearLoadedRecord={() => setLoadedToolSession(null)}
          />
        )}

      </div>
    </div>
  );
}

export default FilingGenerator;